/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge, Icon, Tooltip } from "antd";
import Axios, { AxiosError } from "axios";
import React, { memo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, useHistory, withRouter } from "react-router-dom";

import { getCompanyTerritoryTypes, hasFeature, setActiveApp } from "../../../Actions/Actions";
import * as windowActions from "../../../Actions/WindowActions";
import { ActiveApp } from "../../../Enums/Enums";
import { OktaGroups } from "../../../Enums/Okta";
import { ScoutFeature } from "../../../Enums/ScoutFeature";
import { IFeature, IServiceResult, IUser } from "../../../Models/Models";
import { showActiveUsersSelector } from "../../../Reducers/Selectors/UserSelectors";
import { setShowActiveUsers } from "../../../View/Products/Actions/UserActions";
import { UserAvatar } from "../../Common";
import { NavDropdown, NavItem } from "../Layout/Navigation";

interface IUserDropdownProps extends RouteComponentProps {
    isScout: boolean;
    user: IUser;
    showActiveUsers: boolean;
    fromNewUI?: boolean;
}

const UserDropdown: React.FunctionComponent<IUserDropdownProps> = memo(
    (props: IUserDropdownProps) => {
        /** misc */
        const dispatch = useDispatch();
        const history = useHistory();
        const formRef = useRef<HTMLFormElement>(null);
        /** selectors */
        const showActiveUsers: boolean = useSelector(showActiveUsersSelector);
        /** state */
        const [viewingUserName, setViewingUserName] = useState<string>("");
        /** misc */
        const isBuxtonCorpUser = props.user?.oktaGroups?.some((group: string) => group === OktaGroups.BUXTONCORP);
        const isMultiAccountUser = props.user?.oktaGroups?.some((group: string) => group === OktaGroups.MULTIACCOUNTUSERS);
        const userIsInOktaGroup = isBuxtonCorpUser || isMultiAccountUser;
        const hasIntercomConversation: boolean = props.user.features.some(
            (f: IFeature) => f.name === ScoutFeature.IntercomConversation
        );
        /** effects */
        useEffect(() => {
            if (userIsInOktaGroup) {
                Axios.get<IServiceResult<string>>(`/BuxtonView/QueryUserNameByUserID?userId=${props.user.userID}&cid=${props.user.company.cid}`)
                    .then((response) => {
                        setViewingUserName(response.data.data);
                    })
                    .catch((error: AxiosError) => {
                        console.log("error retrieving user name");
                    });
            }
        }, []);

        const launchSegmentationGuide = (): void => {
            if (props.isScout) {
                (window as any).scout.logic.IntercomLogic.logAction(
                    "scout",
                    "top-nav",
                    "click",
                    "segmentation-guide"
                );
            }
            window.open("/Experian/Guide");
        };

        const launchKnowledgeBase = (): void => {
            if (props.isScout) {
                (window as any).scout.logic.IntercomLogic.logAction(
                    "scout",
                    "top-nav",
                    "click",
                    "knowledge-base"
                );
            }
            window.open("http://help.buxtonco.com/");
        };

        const launchUserSettings = (): void => {
            const urlPrefix = props.isScout ? "" : `${window.location.origin}/Home/Scout#`;
            const initialUserSettingsView = !isBuxtonCorpUser && !isMultiAccountUser
                ? `${urlPrefix}/settings/Profile`
                : `${urlPrefix}/settings/Preferences`;

            if (hasFeature(props.user, ScoutFeature.Scout4)) {
                props.isScout ? history.push(initialUserSettingsView)
                    : window.location.href = initialUserSettingsView;
            } else {
                !isBuxtonCorpUser && !isMultiAccountUser ? history.push("/Settings/Profile") : history.push("/Settings/Preferences");
            }
        };

        const launchUserManagement = (): void => {
            dispatch(setActiveApp(ActiveApp.SETTINGS));
            dispatch(getCompanyTerritoryTypes());
            history.push("/settings/ManageUsers");
        };

        const launchTerritoryManagement = (): void => {
            dispatch(setActiveApp(ActiveApp.SETTINGS));
            dispatch(getCompanyTerritoryTypes());
            history.push("/settings/ManageTerritories");
        };

        const toggleActiveUsers = (): void => {
            dispatch(setShowActiveUsers(!showActiveUsers));
        };

        const onLogoutClick = (): void => {
            if (props.isScout) {
                (window as any).Intercom("shutdown");
            }
            formRef.current.submit();
        };

        const avatarAndArrow: JSX.Element = (
            <>
                <UserAvatar user={props.user} />
                <Icon
                    style={{
                        color: "white"
                    }}
                    type="down"
                />
            </>
        );

        return (
            <>
                <NavDropdown
                    isMainNavItem={true}
                    className="border-right"
                    trigger={
                        props.user
                            && (isBuxtonCorpUser || isMultiAccountUser)
                            ? (
                                <Badge
                                    count={
                                        <Tooltip
                                            placement="bottomRight"
                                            title={
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                    }}
                                                >
                                                    <strong>Viewing as</strong>
                                                    <span style={{ padding: "4px" }}>
                                                        Company: {props.user.company.name}
                                                    </span>
                                                    {!isMultiAccountUser
                                                        ? (
                                                            <span style={{ padding: "2px 4px 2px 4px" }}>
                                                                Username: {viewingUserName}
                                                            </span>
                                                        )
                                                        : null}
                                                </div>
                                            }>
                                            <img
                                                style={{
                                                    height: "28px",
                                                    width: "28px",
                                                    top: "28px",
                                                    right: "45px",
                                                }}
                                                src={props.user.company.logoPathSmall}
                                            />
                                        </Tooltip>
                                    }>
                                    {avatarAndArrow}
                                </Badge>
                            )
                            : avatarAndArrow
                    }
                    triggerType="NavItem"
                    dropdownPosition="right"
                    triggerWidth={42}
                    triggerPadding="padding: 0;">
                    {props.user
                        && props.isScout
                        && hasIntercomConversation
                        && (
                            <NavItem
                                type="NavDropdownItem"
                                onClick={checkFullScreenForIntercom}
                                className="buxton-intercom">
                                Chat with Support
                            </NavItem>
                        )}
                    <NavDropdown
                        trigger={
                            <>
                                Learn
                                <Icon type="right" />
                            </>
                        }
                        triggerType="NavDropdownItem"
                        dropdownPosition="left">
                        <NavItem type="NavDropdownItem" onClick={launchKnowledgeBase}>
                            Knowledge Base
                        </NavItem>
                        <NavItem
                            type="NavDropdownItem"
                            onClick={launchSegmentationGuide}>
                            Segmentation Guide
                        </NavItem>
                    </NavDropdown>
                    <NavItem type="NavDropdownItem" onClick={toggleActiveUsers}>
                        Active Users
                    </NavItem>
                    <NavItem type="NavDropdownItem" onClick={launchUserSettings}>
                        User Settings
                    </NavItem>
                    {userIsInOktaGroup && (
                        <NavItem
                            type="NavDropdownItem"
                            onClick={() => {
                                (window as any).Intercom("shutdown");
                                window.location.pathname = "/Home/Splash";
                            }}>
                            Change Viewing User
                        </NavItem>
                    )}
                    <NavItem
                        type="NavDropdownItem"
                        onClick={onLogoutClick}>
                        <form ref={formRef} name="logoutForm" action="/account/logout" method="POST">
                            <button>Log Out</button>{" "}
                            {/* 'submit' is the default 'type' for the 'button' element */}
                        </form>
                    </NavItem>
                    {/* START HACK (GDB) */}
                    {/* Used to trigger some specific link clicks in the LeftNav of Scout. Long story short, getting to these pages from outside of React is a pain in the butt. Once we get all the left nav stuff in SCOUT
                into React, we can remove this hack. */}
                    <NavItem
                        style={{ display: "none" }}
                        type="NavDropdownItem"
                        onClick={launchUserManagement}
                        id="user-dropdown-launch-user-management"
                    >
                        User Management
                    </NavItem>
                    <NavItem
                        style={{ display: "none" }}
                        type="NavDropdownItem"
                        onClick={launchTerritoryManagement}
                        id="user-dropdown-launch-territory-management"
                    >
                        Territory Management
                    </NavItem>
                    {/* END HACK */}
                </NavDropdown>
            </>
        );
    }
);

const checkFullScreenForIntercom = (): void => {
    if (windowActions.isInFullScreen()) {
        windowActions.exitFullScreen();
    }
};

//Using React.memo messes up the displayName property of the component - set it here
UserDropdown.displayName = "UserDropdown";

export default withRouter(UserDropdown);
